/**
 * Transforms complex answer objects into simple key:value pairs for database storage
 * Handles different question types and flattens nested structures
 */

/**
 * Flattens survey answers to simple key:value pairs
 * @param {object} answers - Raw survey answers
 * @param {array} questions - All survey questions
 * @returns {object} Flattened answers with simple string values
 */
export function flattenAnswers(answers, questions) {
  const flattened = {};

  questions.forEach((question) => {
    const answer = answers[question.id];

    // Skip unanswered questions
    if (answer === undefined || answer === null || answer === '') {
      return;
    }

    const key = question.id;

    switch (question.type) {
      case 'select':
      case 'radio':
      case 'number':
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        // Simple values - store as-is
        flattened[key] = String(answer);
        break;

      case 'checkbox':
        // Checkbox: { selections: ['opt1', 'opt2'], otherText: 'custom text' }
        if (answer.selections && Array.isArray(answer.selections)) {
          const selections = [...answer.selections];

          // If "outro" is selected and there's text, merge it
          if (selections.includes('outro') && answer.otherText?.trim()) {
            // Replace 'outro' with the actual text
            const index = selections.indexOf('outro');
            selections[index] = answer.otherText.trim();
          }

          // Join with semicolon
          flattened[key] = selections.join(';');
        } else {
          flattened[key] = '';
        }
        break;

      case 'sortable':
        // Sortable: { selections: ['opt1', 'opt2'], order: ['opt2', 'opt1'] }
        // Store the ordered list (order array is what matters)
        if (answer.order && Array.isArray(answer.order)) {
          flattened[key] = answer.order.join(';');
        } else if (answer.selections && Array.isArray(answer.selections)) {
          // Fallback to selections if order doesn't exist
          flattened[key] = answer.selections.join(';');
        } else {
          flattened[key] = '';
        }
        break;

      case 'rating':
        // Rating: { 'statement-id-1': 3, 'statement-id-2': 5 }
        // Store as separate columns: question-id_statement-id
        if (typeof answer === 'object') {
          Object.entries(answer).forEach(([statementId, rating]) => {
            flattened[`${key}_${statementId}`] = String(rating);
          });
        }
        break;

      default:
        // Unknown type - store as JSON string
        flattened[key] = typeof answer === 'object'
          ? JSON.stringify(answer)
          : String(answer);
    }
  });

  return flattened;
}

/**
 * Gets all possible database column names from questions
 * Useful for generating database schema
 * @param {array} questions - All survey questions
 * @returns {array} Array of { columnName, questionType, questionText, statementText }
 */
export function getDatabaseColumns(questions) {
  const columns = [];

  questions.forEach((question) => {
    switch (question.type) {
      case 'rating':
        // Rating questions create multiple columns (one per statement)
        if (question.statements && Array.isArray(question.statements)) {
          question.statements.forEach((statement) => {
            columns.push({
              columnName: `${question.id}_${statement.id}`,
              questionType: 'rating',
              questionText: question.question,
              statementText: statement.text,
              maxLength: 2 // Rating values are typically 1-5
            });
          });
        }
        break;

      case 'text':
      case 'email':
      case 'url':
        columns.push({
          columnName: question.id,
          questionType: question.type,
          questionText: question.question,
          maxLength: 255
        });
        break;

      case 'number':
        columns.push({
          columnName: question.id,
          questionType: question.type,
          questionText: question.question,
          maxLength: 20
        });
        break;

      case 'checkbox':
      case 'sortable':
        // Multiple selections - can be long
        columns.push({
          columnName: question.id,
          questionType: question.type,
          questionText: question.question,
          maxLength: 1000 // Longer for multiple selections
        });
        break;

      default:
        // select, radio, and other simple types
        columns.push({
          columnName: question.id,
          questionType: question.type,
          questionText: question.question,
          maxLength: 255
        });
    }
  });

  return columns;
}
